import { defineComponent, h, nextTick } from 'vue';
import { QTabPanels, QTabPanel } from 'quasar';
import BaseComponent from './BaseComponentMixin.jsx';
import utils from '../../Shared/utils.jsx';

export default defineComponent({
    name: 'dynamic-tab-content',
    mixins: [BaseComponent],
    components: {
        QTabPanels,
        QTabPanel
    },
    data: function () {
        return {
            sourceraw: null,
            sourceurl: null,
            dynamictabs: null,
            tab_state: {
                index: 0
            },
            selected_tab: -1,
            selected_tab_expr: null,
            model: null,
            tabRenderCount: {},
            renderedTabs: new Set(),
            // Visited tab indexes kept mounted when PersistViewSpace is true (replaced for reactivity).
            persistedTabs: new Set()
        }
    },
    created() {
        if (this.controlData.SourceType === 'Raw' && this.controlData.SourceRaw) {
            this.sourceraw = utils.compileObject(this, this.controlData.SourceRaw);
        }
        else if (this.controlData.SourceType === 'Url' && this.controlData.SourceUrl) {
            this.sourceurl = utils.compile(this, this.controlData.SourceUrl);
        }

        if (this.controlData.ActiveTabIndex)
            this.selected_tab_expr = utils.compileExpression(this, this.controlData.ActiveTabIndex);

        if (this.controlData.ListenForTabEvents) {
            this.$on('Action-Close', this.closeTabAction);
            this.$on('Action-RenameTab', this.performRenameTab);

            // Subscribe by name so Target can be directed to a specific control
            if (this.controlData.Name) {
                this.$on(`Action-Close:${this.controlData.Name}`, this.closeTabAction);
                this.$on(`Action-RenameTab:${this.controlData.Name}`, this.performRenameTab);
            }
        }

        if (this.controlData.DynamicTabsControl) {
            const dynamictabsexpn = utils.compileExpression(this, this.controlData.DynamicTabsControl);
            this.dynamictabs = utils.evaluate(dynamictabsexpn, this);
        }
    },
    unmounted() {
        if (this.controlData.ListenForTabEvents) {
            this.$off('Action-Close', this.closeTabAction);
            this.$off('Action-RenameTab', this.performRenameTab);

            // Subscribe by name so Target can be directed to a specific control
            if (this.controlData.Name) {
                this.$off(`Action-Close:${this.controlData.Name}`, this.closeTabAction);
                this.$off(`Action-RenameTab:${this.controlData.Name}`, this.performRenameTab);
            }
        }
    },
    //Mounted Replaced with preRenderComplete
    computed: {
        activeTabIndex: function () {
            let index;
            if (this.selected_tab_expr)
                index = utils.evaluate(this.selected_tab_expr, this);
            else
                return 0;

            if (typeof index === 'string')
                return parseInt(index);
            else
                return index;
        },
        Menus: function () {
            return utils.document_cache.menus;
        },
        sourcerawvalue: function () {
            if (!this.todisplay)
                return '';

            return (this.sourceraw) ? utils.evaluateObject(this.sourceraw, this) : null;
        },
        sourceurlvalue: function () {
            if (!this.todisplay)
                return '';

            return this.sourceurl ? utils.evaluate(this.sourceurl, this) : '';
        },
        // This is the total number of child controls a component has. This should be overridden in child controls that don't 
        // have their children stored on a property called Controls.
        totalItems() {
            let count = 0;

            // These dynamically load, so I can't wait for them at initialization
            //if (this.model && typeof this.model === 'object') {
            //    if (Array.isArray(this.model))
            //        count = this.model.length;
            //    else
            //        count = Object.keys(this.model).length;
            //}

            // Add one to include the component itself.
            if (Array.isArray(this.controlData.Controls))
                count = (this.controlData.Controls.length * count) + 1;

            return count;
        },
        sources() {
            let sources = [];

            if (this.model && typeof this.model === 'object') {
                if (Array.isArray(this.model))
                    for (let i = 0; i < this.model.length; i++) {
                        sources.push( this.model[i]);
                    }
                else
                    Object.keys(this.model).forEach(key => {
                        sources.push(this.model[key]);
                    });
            }

            return sources;
        },
        persistViewSpace() {
            return this.controlData?.PersistViewSpace === true;
        },
        tabCount() {
            if (this.sources.length > 0)
                return this.sources.length;
            return Array.isArray(this.controlData?.Controls) ? this.controlData.Controls.length : 0;
        }
    },
    watch: {
        sourcerawvalue: function () {
            this.Refresh();
        },
        sourceurlvalue: function () {
            this.Refresh();
        },
        activeTabIndex: function (newval, oldval) {
            console.log(`DynamicTabContent ${this.name} activeTabIndex changed from ${oldval} to ${newval}`);
            //if (!newval)
            //    debugger;
            this.doChangeSelectedTab(newval);
        },
        //selected_tab: function (newval, oldval) {
        //    utils.debug(`DynamicTabContent ${this.name} selected_tab (watch) has changed from ${oldval} to ${newval}`);
        //},
    },
    methods: {
        async preRenderComplete() {
            await this.Refresh();
            this.finishRenderHandler(this);
        }, 
        async Refresh() {
            if (this.sourceraw)
                try {
                    this.model = this.sourcerawvalue;
                }
                catch (e) {
                    utils.warn('DynamicTabContent failed to evaluate SourceRaw ' + this.controlData.SourceRaw + ': ' + e);
                }
            else if (this.sourceurl)
                try {
                    this.model = await utils.api.get(this.sourceurlvalue);
                }
                catch (e) {
                    utils.warn('DynamicTabContent failed to evaluate SourceUrl ' + this.controlData.SourceUrl + ': ' + e);
                }

            this.renderedTabs.clear();
            this.tabRenderCount = {};
            this.prunePersistedTabs();
            nextTick(() => {
                this.selected_tab = this.activeTabIndex;
                this.ensurePersistedTab(this.selected_tab);
            });
        },
        onChangeTagIndex(index) {
            if (!(index === undefined)) {
                this.selected_tab = index;
                this.ensurePersistedTab(index);
            }
        },
        ensurePersistedTab(index) {
            if (!this.persistViewSpace || index == null || index < 0)
                return;
            if (this.persistedTabs.has(index))
                return;
            const next = new Set(this.persistedTabs);
            next.add(index);
            this.persistedTabs = next;
        },
        prunePersistedTabs() {
            if (!this.persistViewSpace) {
                if (this.persistedTabs.size > 0)
                    this.persistedTabs = new Set();
                return;
            }

            const max = this.tabCount;
            const next = new Set();
            for (const idx of this.persistedTabs) {
                if (idx >= 0 && idx < max)
                    next.add(idx);
            }
            const active = this.activeTabIndex;
            if (active >= 0 && active < max)
                next.add(active);
            this.persistedTabs = next;
        },
        persistedIndexes() {
            const max = this.tabCount;
            return [...this.persistedTabs]
                .filter(idx => idx >= 0 && idx < max)
                .sort((a, b) => a - b);
        },
        buildSourceTabElements(k, src) {
            const elements = [];

            for (let i = 0; i < this.controlData.Controls.length; i++) {
                const c = this.controlData.Controls[i];
                if (!(typeof c === 'object'))
                    continue;

                let DynamicControl = utils.getDynamicComponent(h, c);

                if (!DynamicControl)
                    DynamicControl = 'default-unknown';

                if (!c.$objectId) c.$objectId = utils.generateUUID();

                let id = `${c.$objectId}_${src.ID}_${i}`;

                elements.push(
                    <DynamicControl
                        key={id}
                        type={c.ControlType}
                        name={c.ControlData.Name || `unnamed_source_${k}_${i}`}
                        root={this.root}
                        asbase={true}
                        parentType="VerticalStack"
                        controlData={c.ControlData}
                        controlURL={c.ControlURL}
                        accessPermission={c.AccessPermission}
                        sourceData={src}
                        onFinishedRender={(reference) => this.handleFinishedRender(reference, k)}
                        controlName={c.Name}
                        scopeitems={this.scopeitems}
                        controlscope={this.controlscope}
                        cacheControl={c.CacheControl}
                        controlEvents={c.Events}
                    >
                    </DynamicControl>
                );
            }

            return elements;
        },
        buildLiteralTabElement(i) {
            const c = this.controlData.Controls[i];
            if (!(typeof c === 'object'))
                return null;

            let DynamicControl = utils.getDynamicComponent(h, c);

            if (!DynamicControl)
                DynamicControl = 'default-unknown';

            if (!c.$objectId) c.$objectId = utils.generateUUID();

            return (
                <DynamicControl
                    key={c.$objectId}
                    type={c.ControlType}
                    name={c.ControlData.Name || `unnamed_literal_${i}`}
                    root={this.root}
                    asbase={true}
                    parentType="VerticalStack"
                    controlData={c.ControlData}
                    controlURL={c.ControlURL}
                    accessPermission={c.AccessPermission}
                    onFinishedRender={(reference) => this.finishRenderHandler(reference)}
                    controlName={c.Name}
                    scopeitems={this.scopeitems}
                    controlscope={this.controlscope}
                    cacheControl={c.CacheControl}
                    controlEvents={c.Events}
                >
                </DynamicControl>
            );
        },
        async performRenameTab(action) {
            if (this.dynamictabs) {
                this.dynamictabs.performRenameTab(action);
                return;
            }

            utils.log(`DynamicTabContent RenameTab ${action.ActionData.TabTitle}`);

            if (action.ActionData && action.ActionData.Debug && action.ActionData.Debug.BreakPoint) debugger;

            if (action.tabtitle) {
                const title = utils.evaluate(action.tabtitle, action.context);

                await utils.executeAndCompileAllActions(this.controlData.RenameActions, { TabTitle: title, Index: this.selected_tab }, this);

                try {
                    await utils.complete(action);
                }
                catch (e) { }

                // Complete the promise for the executeAction method
                action.FinishFunc(true);
            }
        },
        async closeTabAction(action) {
            // This handles the Close action which is emitted by UIAction_Close, which is typically broadcast from a child
            if (this.dynamictabs) {
                this.dynamictabs.closeTabAction(action);
                return;
            }

            utils.log(`DynamicTabContent closeTabAction`);

            if (action.ActionData.Debug && action.ActionData.Debug.BreakPoint) debugger;

            await utils.executeAndCompileAllActions(this.controlData.CloseActions, { Data: this.selected_tab }, this);

            try {
                await utils.complete(action);
            }
            catch (e) { }

            // Complete the promise for the executeAction method
            action.FinishFunc(true)
        },
        getItemByIndex(index) {
            if(index >= 0 && index < this.sources.length)
                return this.sources[index];
            return null;
        },
        updateTabRenderStatus(index) {
            if(!this.renderedTabs.has(index)) {
                if(!this.tabRenderCount[index])
                    this.tabRenderCount[index] = 1;
                else
                    this.tabRenderCount[index] = this.tabRenderCount[index] + 1;
                
                if(this.tabRenderCount[index] === this.controlData.Controls.length) {
                    this.renderedTabs.add(index);
                    this.$nextTick(async () => {
                        await utils.executeAndCompileAllActions(this.controlData.OnActivateActions, { Data: this.getItemByIndex(this.selected_tab) }, this);
                    });
                }
                
            }
        },
        handleFinishedRender(reference, index) {
            this.finishRenderHandler(reference);
            this.updateTabRenderStatus(index);
        },

        async doChangeSelectedTab(newval) {
            if (this.controlData.OnDeActivateActions && this.selected_tab >= 0) {
                const data = this.getItemByIndex(this.selected_tab);
                if (data)
                    utils.executeAndCompileAllActions(this.controlData.OnDeActivateActions, { Data: data }, this);
            }

            await nextTick();

            //utils.debug(`DynamicTabContent ${this.name} changing selected_tab from ${this.selected_tab} to ${newval}`);
            this.selected_tab = newval;
            this.ensurePersistedTab(newval);

            if (this.renderedTabs.has(this.selected_tab)) {
                await nextTick();

                utils.executeAndCompileAllActions(this.controlData.OnActivateActions, { Data: this.getItemByIndex(this.selected_tab) }, this);
            }            
        },
    },
    props: {
    },
    render() {
        if (!this.todisplay || !this.controlData) {
            return null;
        }
        //utils.log(`DynamicTabContent ${this.Name} rendering; model:${this.model ? this.model.length : 0}`);
        try {
            const style = {
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                padding: 0,
                //flexGrow: "1",
                ...this.sizeStyle,
                ...utils.resolveStyleHints(this.styleHints, this),
            };

            const containerClass = {
                'q-ma-none': true,
                'q-pa-none': true,
                'c-DynamicTabContent': true,
                [`c-name-${this.name || 'unnamed'}`]: true
            };

            // PersistViewSpace: keep visited tabs mounted; toggle visibility with v-show.
            if (this.persistViewSpace) {
                const items = [];
                const indexes = this.persistedIndexes();

                if (this.sources.length > 0) {
                    for (const k of indexes) {
                        const src = this.sources[k];
                        if (!src)
                            continue;

                        if (!src.ID)
                            src.ID = utils.generateUUID();

                        items.push(
                            <div key={src.ID} v-show={this.selected_tab === k} class="q-pa-none" style={style}>
                                {this.buildSourceTabElements(k, src)}
                            </div>
                        );
                    }
                }
                else {
                    for (const i of indexes) {
                        const element = this.buildLiteralTabElement(i);
                        if (!element)
                            continue;

                        const c = this.controlData.Controls[i];
                        items.push(
                            <div key={c.$objectId || i} v-show={this.selected_tab === i} class="q-pa-none" style={style}>
                                {element}
                            </div>
                        );
                    }
                }

                return (
                    <div class={containerClass} style={style}>
                        {items}
                    </div>
                );
            }

            //utils.log(` * * * DynamicTabContent:${this.name} style display:${style.display},flexDirection:${style.flexDirection},flexGrow:${style.flexGrow},overflow:${style.overflow} * * *`);

            const items = [];

            if (this.sources.length > 0)
                // Repeat all controls for each element in the source
                for (let k = 0; k < this.sources.length; k++) {
                    const src = this.sources[k];

                    if (!src.ID)
                        src.ID = utils.generateUUID();

                    items.push(
                        <QTabPanel key={src.ID} name={k} class="q-pa-none" style={style}>
                            {this.buildSourceTabElements(k, src)}
                        </QTabPanel>
                    );
                }
            else
                // Emit one control per page
                for (let i = 0; i < this.controlData.Controls.length; i++) {
                    const element = this.buildLiteralTabElement(i);
                    if (!element)
                        continue;

                    items.push(
                        <QTabPanel key={i} name={i} class="q-pa-none" style={style}>
                            {element}
                        </QTabPanel>
                    );
                }

            return (
                <QTabPanels
                    class={containerClass}
                    style={style}
                    modelValue={this.selected_tab}
                    onUpdate:modelValue={(v) => this.onChangeTagIndex(v)}
                    animated={false}
                    keepAlive
                >
                    {items}
                </QTabPanels>
            );
        }
        catch (err) {
            console.error(`Exception in DynamicTabContent`, err);
            return <div>Exception in DynamicTabContent: {err.message}</div>;
        }
    }
});

//on-change={ (index) => this.onChangeTagIndex(index) }
